// One recording writer per observer in this origin/browser storage partition.
// Browser-managed ownership has no heartbeat expiry: a sleeping tab must not
// become a second writer when it wakes. Closing/crashing the document releases
// the lock. Network drains do not hold this lock; queue transactions protect them.
export interface RecordingLock { readonly signal: AbortSignal; assert(): void; release(): void }

export function acquireRecording(observerId: string): Promise<RecordingLock> {
  if (!navigator.locks) return Promise.reject(new Error("This browser cannot safely coordinate recording tabs. Use an updated browser over HTTPS."));
  return new Promise((resolve, reject) => {
    void navigator.locks.request(`field-observation.recording:${observerId}`, { ifAvailable: true }, async (lock) => {
      if (!lock) {
        reject(new Error("You are already recording in another tab. Continue there, or close that tab and resume here."));
        return;
      }
      let active = true;
      const stopped = new AbortController();
      let unlock!: () => void;
      const released = new Promise<void>(r => { unlock = r; });
      resolve({
        signal: stopped.signal,
        assert() { if (!active) throw new Error("Recording ownership ended. Resume the saved session before recording again."); },
        release() { active = false; stopped.abort(); unlock(); },
      });
      await released;
    }).catch(reject);
  });
}
